<template>
  <div class="saved-views-page">
    <!-- Header -->
    <div class="header-section">
      <div class="header-content">
        <div class="brand-header">
          <img 
            src="@/assets/accessiq-logo.png" 
            alt="AccessIQ" 
            class="accessiq-logo-img"
          />
          <span class="brand-subtitle">Saved Views</span>
        </div>
        <p class="page-description">
          Save and manage your custom filter configurations for quick access to specific regulatory data.
        </p>
      </div>
      <div class="header-actions">
        <button @click="showCreateModal = true" class="btn btn-primary">
          <i class="fas fa-plus mr-2"></i>
          New View
        </button>
        <router-link to="/regulatory-intelligence" class="btn btn-secondary">
          <i class="fas fa-compass mr-2"></i>
          Dashboard
        </router-link>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fas fa-bookmark fa-spin fa-3x"></i>
        <p>Loading saved views...</p>
      </div>
    </div>

    <!-- Views Content -->
    <div v-else class="views-content">
      <!-- Favorites Section -->
      <div v-if="favoriteViews.length > 0" class="views-section">
        <h2 class="section-title">
          <i class="fas fa-star mr-2"></i>
          Favorites
        </h2>
        <div class="views-grid">
          <div 
            v-for="view in favoriteViews" 
            :key="view.id" 
            class="view-card favorite"
          >
            <div class="view-header">
              <button 
                @click="toggleFavorite(view)" 
                class="favorite-btn active"
                title="Remove from favorites"
              >
                <i class="fas fa-star"></i>
              </button>
              <div class="view-actions">
                <button @click="editView(view)" class="action-btn" title="Edit">
                  <i class="fas fa-edit"></i>
                </button>
                <button @click="deleteView(view)" class="action-btn delete" title="Delete">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
            <h3 class="view-name">{{ view.name }}</h3>
            <p class="view-description">{{ view.description || 'No description' }}</p>
            <div class="view-tags">
              <span 
                v-for="tag in view.display_tags.slice(0, 4)" 
                :key="tag" 
                class="tag"
              >
                {{ tag }}
              </span>
              <span v-if="view.display_tags.length > 4" class="more-tags">
                +{{ view.display_tags.length - 4 }}
              </span>
            </div>
            <div class="view-footer">
              <span class="view-role">
                <i class="fas fa-user-tag"></i>
                {{ view.role_filter_display }}
              </span>
              <span class="view-access" v-if="view.access_count > 0">
                {{ view.access_count }} views
              </span>
            </div>
            <button @click="applyView(view)" class="apply-btn">
              <i class="fas fa-filter mr-2"></i>
              Apply View
            </button>
          </div>
        </div>
      </div>

      <!-- All Views Section -->
      <div class="views-section">
        <h2 class="section-title">
          <i class="fas fa-bookmark mr-2"></i>
          All Views
        </h2>
        
        <div v-if="regularViews.length === 0 && favoriteViews.length === 0" class="empty-state">
          <i class="fas fa-bookmark fa-4x"></i>
          <h3>No Saved Views Yet</h3>
          <p>Create your first saved view to quickly access filtered regulatory data.</p>
          <button @click="showCreateModal = true" class="btn btn-primary">
            <i class="fas fa-plus mr-2"></i>
            Create First View
          </button>
        </div>
        
        <div v-else class="views-grid">
          <div 
            v-for="view in regularViews" 
            :key="view.id" 
            class="view-card"
            :class="{ 'shared': view.is_shared }"
          >
            <div class="view-header">
              <button 
                @click="toggleFavorite(view)" 
                class="favorite-btn"
                :class="{ 'active': view.is_favorite }"
                title="Add to favorites"
              >
                <i :class="view.is_favorite ? 'fas fa-star' : 'far fa-star'"></i>
              </button>
              <div class="view-actions">
                <span v-if="view.is_shared" class="shared-badge" title="Shared with organization">
                  <i class="fas fa-users"></i>
                </span>
                <button @click="editView(view)" class="action-btn" title="Edit">
                  <i class="fas fa-edit"></i>
                </button>
                <button @click="deleteView(view)" class="action-btn delete" title="Delete">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
            <h3 class="view-name">{{ view.name }}</h3>
            <p class="view-description">{{ view.description || 'No description' }}</p>
            <div class="view-tags">
              <span 
                v-for="tag in view.display_tags.slice(0, 4)" 
                :key="tag" 
                class="tag"
              >
                {{ tag }}
              </span>
              <span v-if="view.display_tags.length > 4" class="more-tags">
                +{{ view.display_tags.length - 4 }}
              </span>
            </div>
            <div class="view-footer">
              <span class="view-role">
                <i class="fas fa-user-tag"></i>
                {{ view.role_filter_display }}
              </span>
              <span class="view-access" v-if="view.access_count > 0">
                {{ view.access_count }} views
              </span>
            </div>
            <button @click="applyView(view)" class="apply-btn">
              <i class="fas fa-filter mr-2"></i>
              Apply View
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showCreateModal || showEditModal" class="modal-overlay" @click.self="closeModals">
      <div class="modal-content">
        <div class="modal-header">
          <h2>
            <i class="fas fa-bookmark mr-2"></i>
            {{ showEditModal ? 'Edit View' : 'Create New View' }}
          </h2>
          <button @click="closeModals" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>View Name *</label>
            <input 
              v-model="formData.name" 
              type="text" 
              class="form-input"
              placeholder="e.g., Texas High-Impact Rules"
            />
          </div>
          
          <div class="form-group">
            <label>Description</label>
            <textarea 
              v-model="formData.description" 
              class="form-textarea"
              placeholder="Brief description of this view..."
              rows="2"
            ></textarea>
          </div>
          
          <div class="form-group">
            <label>Role Filter</label>
            <select v-model="formData.role_filter" class="form-select">
              <option value="all">All Stakeholders</option>
              <option value="provider">Provider</option>
              <option value="mco">MCO</option>
              <option value="state">State Agency</option>
            </select>
          </div>
          
          <div class="form-group">
            <label>Impact Severity</label>
            <div class="checkbox-group">
              <label class="checkbox-item">
                <input type="checkbox" v-model="formData.filters.impact_severity" value="high">
                <span class="severity-badge high">High</span>
              </label>
              <label class="checkbox-item">
                <input type="checkbox" v-model="formData.filters.impact_severity" value="medium">
                <span class="severity-badge medium">Medium</span>
              </label>
              <label class="checkbox-item">
                <input type="checkbox" v-model="formData.filters.impact_severity" value="low">
                <span class="severity-badge low">Low</span>
              </label>
            </div>
          </div>
          
          <div class="form-group">
            <label>States (leave empty for all)</label>
            <input 
              v-model="formData.statesInput" 
              type="text" 
              class="form-input"
              placeholder="e.g., TX, CA, NY"
            />
            <small class="form-help">Comma-separated state codes</small>
          </div>
          
          <div class="form-group">
            <label class="checkbox-item">
              <input type="checkbox" v-model="formData.filters.federal_only">
              <span>Federal Rules Only</span>
            </label>
          </div>
          
          <div class="form-group">
            <label class="checkbox-item">
              <input type="checkbox" v-model="formData.is_shared">
              <span>Share with organization</span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModals" class="btn btn-secondary">Cancel</button>
          <button 
            @click="saveView" 
            class="btn btn-primary"
            :disabled="!formData.name || saving"
          >
            <i v-if="saving" class="fas fa-spinner fa-spin mr-2"></i>
            {{ showEditModal ? 'Save Changes' : 'Create View' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Apply Results Modal -->
    <div v-if="showResultsModal" class="modal-overlay" @click.self="showResultsModal = false">
      <div class="modal-content results-modal">
        <div class="modal-header">
          <h2>
            <i class="fas fa-filter mr-2"></i>
            {{ appliedViewName }} Results
          </h2>
          <button @click="showResultsModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="applyingView" class="loading-inline">
            <i class="fas fa-spinner fa-spin"></i>
            Loading results...
          </div>
          <div v-else-if="appliedResults.length === 0" class="empty-results">
            <i class="fas fa-inbox"></i>
            <p>No matching events found</p>
          </div>
          <div v-else class="results-list">
            <div 
              v-for="event in appliedResults" 
              :key="event.id" 
              class="result-item"
            >
              <div class="result-header">
                <span class="result-state">{{ event.state }}</span>
                <span class="result-type">{{ event.item_type_display }}</span>
                <span v-if="event.effective_date" class="result-date">
                  {{ formatDate(event.effective_date) }}
                </span>
              </div>
              <h4 class="result-title">{{ event.title }}</h4>
              <p class="result-summary">{{ event.what_changed || event.why_providers_care }}</p>
              <a 
                v-if="event.source_url" 
                :href="event.source_url" 
                target="_blank" 
                class="result-link"
              >
                View Source <i class="fas fa-external-link-alt"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'SavedViews',
  
  data() {
    return {
      loading: true,
      saving: false,
      views: [],
      showCreateModal: false,
      showEditModal: false,
      showResultsModal: false,
      editingView: null,
      
      // Form data
      formData: {
        name: '',
        description: '',
        role_filter: 'all',
        is_shared: false,
        statesInput: '',
        filters: {
          impact_severity: [],
          federal_only: false,
          states: [],
          item_types: []
        }
      },
      
      // Applied view results
      appliedViewName: '',
      appliedResults: [],
      applyingView: false
    };
  },
  
  computed: {
    favoriteViews() {
      return this.views.filter(v => v.is_favorite);
    },
    regularViews() {
      return this.views.filter(v => !v.is_favorite);
    }
  },
  
  mounted() {
    this.loadViews();
  },
  
  methods: {
    async loadViews() {
      this.loading = true;
      try {
        const response = await api.get('/regulatory/saved-views/');
        this.views = response.data.results || response.data;
      } catch (error) {
        console.error('Failed to load saved views:', error);
        this.$toast?.error?.('Failed to load saved views');
      } finally {
        this.loading = false;
      }
    },
    
    async toggleFavorite(view) {
      try {
        await api.post(`/regulatory/saved-views/${view.id}/toggle_favorite/`);
        view.is_favorite = !view.is_favorite;
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
      }
    },
    
    editView(view) {
      this.editingView = view;
      this.formData = {
        name: view.name,
        description: view.description || '',
        role_filter: view.role_filter,
        is_shared: view.is_shared,
        statesInput: (view.filters?.states || []).join(', '),
        filters: {
          impact_severity: view.filters?.impact_severity || [],
          federal_only: view.filters?.federal_only || false,
          states: view.filters?.states || [],
          item_types: view.filters?.item_types || []
        }
      };
      this.showEditModal = true;
    },
    
    async deleteView(view) {
      if (!confirm(`Delete "${view.name}"? This cannot be undone.`)) return;
      
      try {
        await api.delete(`/regulatory/saved-views/${view.id}/`);
        this.views = this.views.filter(v => v.id !== view.id);
        this.$toast?.success?.('View deleted');
      } catch (error) {
        console.error('Failed to delete view:', error);
        this.$toast?.error?.('Failed to delete view');
      }
    },
    
    async applyView(view) {
      this.appliedViewName = view.name;
      this.appliedResults = [];
      this.applyingView = true;
      this.showResultsModal = true;
      
      try {
        const response = await api.post(`/regulatory/saved-views/${view.id}/apply/`);
        this.appliedResults = response.data.events || [];
      } catch (error) {
        console.error('Failed to apply view:', error);
        this.$toast?.error?.('Failed to apply view');
      } finally {
        this.applyingView = false;
      }
    },
    
    async saveView() {
      this.saving = true;
      
      // Parse states from input
      const states = this.formData.statesInput
        .split(',')
        .map(s => s.trim().toUpperCase())
        .filter(s => s.length === 2);
      
      const payload = {
        name: this.formData.name,
        description: this.formData.description,
        role_filter: this.formData.role_filter,
        is_shared: this.formData.is_shared,
        filters: {
          ...this.formData.filters,
          states: states
        }
      };
      
      try {
        if (this.showEditModal && this.editingView) {
          await api.patch(`/regulatory/saved-views/${this.editingView.id}/`, payload);
          this.$toast?.success?.('View updated');
        } else {
          await api.post('/regulatory/saved-views/', payload);
          this.$toast?.success?.('View created');
        }
        
        this.closeModals();
        this.loadViews();
      } catch (error) {
        console.error('Failed to save view:', error);
        this.$toast?.error?.('Failed to save view');
      } finally {
        this.saving = false;
      }
    },
    
    closeModals() {
      this.showCreateModal = false;
      this.showEditModal = false;
      this.editingView = null;
      this.formData = {
        name: '',
        description: '',
        role_filter: 'all',
        is_shared: false,
        statesInput: '',
        filters: {
          impact_severity: [],
          federal_only: false,
          states: [],
          item_types: []
        }
      };
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.saved-views-page {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  background: #f8fafc;
  min-height: 100vh;
}

/* Header */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
}

.brand-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.accessiq-logo-img {
  height: 40px;
  width: auto;
}

.brand-subtitle {
  font-size: 18px;
  color: #64748b;
  font-weight: 500;
}

.page-description {
  color: #64748b;
  margin: 0;
  max-width: 500px;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.loading-spinner {
  text-align: center;
  color: #64748b;
}

.loading-spinner i {
  color: #0d9488;
  margin-bottom: 16px;
}

/* Sections */
.views-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 16px 0;
}

.section-title i {
  color: #0d9488;
}

/* Views Grid */
.views-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

/* View Card */
.view-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  transition: all 0.2s;
}

.view-card:hover {
  border-color: #0d9488;
  box-shadow: 0 4px 12px rgba(13, 148, 136, 0.15);
}

.view-card.favorite {
  border-color: #fbbf24;
  background: linear-gradient(to bottom right, #fffbeb, white);
}

.view-card.shared {
  border-left: 3px solid #0d9488;
}

.view-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.favorite-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #cbd5e1;
  cursor: pointer;
  padding: 4px;
}

.favorite-btn:hover,
.favorite-btn.active {
  color: #fbbf24;
}

.view-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
}

.action-btn:hover {
  color: #0d9488;
}

.action-btn.delete:hover {
  color: #ef4444;
}

.shared-badge {
  color: #0d9488;
  font-size: 14px;
}

.view-name {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 8px 0;
}

.view-description {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 12px 0;
  flex: 1;
}

.view-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.tag {
  background: #ccfbf1;
  color: #0d9488;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.more-tags {
  font-size: 11px;
  color: #94a3b8;
}

.view-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #f1f5f9;
  margin-bottom: 12px;
}

.view-role {
  font-size: 12px;
  color: #64748b;
}

.view-role i {
  margin-right: 4px;
}

.view-access {
  font-size: 12px;
  color: #94a3b8;
}

.apply-btn {
  width: 100%;
  padding: 10px;
  background: #0d9488;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.apply-btn:hover {
  background: #0f766e;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 24px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.empty-state i {
  color: #cbd5e1;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 18px;
  color: #334155;
  margin: 0 0 8px 0;
}

.empty-state p {
  color: #64748b;
  margin: 0 0 20px 0;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.results-modal {
  max-width: 700px;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  font-size: 18px;
  margin: 0;
  color: #0f172a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 18px;
  color: #64748b;
  cursor: pointer;
}

.close-btn:hover {
  color: #0f172a;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Form */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #334155;
  margin-bottom: 6px;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 14px;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #0d9488;
}

.form-help {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 4px;
}

.checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
}

.checkbox-item input {
  cursor: pointer;
}

.severity-badge {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
}

.severity-badge.high {
  background: #fef2f2;
  color: #dc2626;
}

.severity-badge.medium {
  background: #fef3c7;
  color: #d97706;
}

.severity-badge.low {
  background: #d1fae5;
  color: #059669;
}

/* Results */
.loading-inline {
  text-align: center;
  padding: 40px;
  color: #64748b;
}

.loading-inline i {
  margin-right: 8px;
}

.empty-results {
  text-align: center;
  padding: 40px;
  color: #94a3b8;
}

.empty-results i {
  font-size: 32px;
  margin-bottom: 12px;
  display: block;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.result-item {
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 3px solid #0d9488;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.result-state {
  background: #0d9488;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.result-type {
  font-size: 12px;
  color: #64748b;
}

.result-date {
  font-size: 12px;
  color: #94a3b8;
  margin-left: auto;
}

.result-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 8px 0;
}

.result-summary {
  font-size: 13px;
  color: #64748b;
  margin: 0 0 8px 0;
  line-height: 1.5;
}

.result-link {
  font-size: 12px;
  color: #0d9488;
  text-decoration: none;
}

.result-link:hover {
  text-decoration: underline;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  border: none;
  transition: all 0.2s;
}

.btn-primary {
  background: #0d9488;
  color: white;
}

.btn-primary:hover {
  background: #0f766e;
}

.btn-primary:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f1f5f9;
  color: #334155;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

/* Responsive */
@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    gap: 16px;
  }
  
  .views-grid {
    grid-template-columns: 1fr;
  }
}
</style>
